import { ClientInputError, type BillingAddressInput, type ClientCreateInput, type ClientInput, type ContactInput, type RecordState } from "./types";
import { normalizeInvoicePrefix } from "../../shared/invoice-prefix";
import { isValidEmailAddress } from "../../shared/email";
import { normalizeContactPhone } from "../../shared/contact-phone";

function object(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) throw new ClientInputError("Invalid input");
  return value as Record<string, unknown>;
}

function exactKeys(value: Record<string, unknown>, allowed: readonly string[]) {
  if (Object.keys(value).some((key) => !allowed.includes(key))) throw new ClientInputError("Invalid input");
}

function text(value: unknown, maximum: number): string {
  if (typeof value !== "string") throw new ClientInputError("Invalid input");
  const normalized = value.trim();
  if (!normalized || normalized.length > maximum) throw new ClientInputError("Invalid input");
  return normalized;
}

function optionalText(value: unknown, maximum: number): string | null {
  if (value === undefined || value === null || (typeof value === "string" && value.trim() === "")) return null;
  return text(value, maximum);
}

export function parseState(value: string | undefined): RecordState {
  if (value === undefined || value === "active") return "active";
  if (value === "archived") return "archived";
  throw new ClientInputError("Invalid state");
}

export function parseSearchQuery(value: string | undefined): string {
  if (value === undefined) return "";
  if (value.length > 200) throw new ClientInputError("Invalid search");
  return value.trim();
}

export function parseIdentifier(value: string): string {
  if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)) {
    throw new ClientInputError("Invalid identifier");
  }
  return value;
}

export function parseClientInput(value: unknown): ClientInput {
  const input = object(value);
  exactKeys(input, ["displayName", "invoicePrefix", "billingAddress"]);
  return { displayName: text(input.displayName, 200), ...(input.invoicePrefix === undefined ? {} : {invoicePrefix:prefix(input.invoicePrefix)}), billingAddress: parseBillingAddress(input.billingAddress) };
}

function prefix(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value !== "string") throw new ClientInputError("Invoice prefix must be 2–8 letters or digits");
  const normalized = normalizeInvoicePrefix(value);
  if (!normalized) throw new ClientInputError("Invoice prefix must be 2–8 letters or digits");
  return normalized;
}

export function parseClientCreateInput(value: unknown): ClientCreateInput {
  const input = object(value);
  exactKeys(input, ["displayName", "invoicePrefix", "billingAddress", "primaryContact", "additionalContacts"]);
  const primaryContact = input.primaryContact === undefined || input.primaryContact === null
    ? null
    : parseContactFields(input.primaryContact);
  if (input.additionalContacts !== undefined && !Array.isArray(input.additionalContacts)) throw new ClientInputError("Invalid input");
  const additionalContacts = (input.additionalContacts ?? []).map(parseContactFields);
  if (additionalContacts.length > 10) throw new ClientInputError("Invalid input");
  return {
    displayName: text(input.displayName, 200),
    invoicePrefix: prefix(input.invoicePrefix),
    billingAddress: parseBillingAddress(input.billingAddress),
    primaryContact,
    additionalContacts,
  };
}

function parseBillingAddress(value: unknown): BillingAddressInput {
  if (value === undefined || value === null) {
    return { line1: null, line2: null, locality: null, region: null, postalCode: null, country: null };
  }
  const input = object(value);
  exactKeys(input, ["line1", "line2", "locality", "region", "postalCode", "country"]);
  return {
    line1: optionalText(input.line1, 200),
    line2: optionalText(input.line2, 200),
    locality: optionalText(input.locality, 120),
    region: optionalText(input.region, 120),
    postalCode: optionalText(input.postalCode, 40),
    country: optionalText(input.country, 100),
  };
}

function parseContactFields(value: unknown): Omit<ContactInput, "isPrimary"> {
  const input = object(value);
  exactKeys(input, ["name", "email", "phone", "billingAddress"]);
  const email = optionalText(input.email, 320);
  if (email && !isValidEmailAddress(email)) throw new ClientInputError("Invalid input");
  const name = optionalText(input.name, 200) ?? "";
  if (!name && !email) throw new ClientInputError("Enter a contact name or email address.");
  const phone = normalizeContactPhone(optionalText(input.phone, 50) ?? "");
  if (phone && phone.length > 50) throw new ClientInputError("Invalid phone");
  return { name, email, phone, ...(input.billingAddress === undefined ? {} : { billingAddress: parseBillingAddress(input.billingAddress) }) };
}

export function parseContactInput(value: unknown): ContactInput {
  const input = object(value);
  exactKeys(input, ["name", "email", "phone", "isPrimary", "billingAddress"]);
  if (input.isPrimary !== undefined && typeof input.isPrimary !== "boolean") throw new ClientInputError("Invalid input");
  const { isPrimary, ...fields } = input;
  return {
    ...parseContactFields(fields),
    isPrimary: isPrimary === true,
  };
}

export function parseVersion(value: unknown): number {
  if (!Number.isInteger(value) || Number(value) < 1) throw new ClientInputError("Invalid version");
  return Number(value);
}

export function parseMutationBody(value: unknown): { version: number } {
  const input = object(value);
  exactKeys(input, ["version"]);
  return { version: parseVersion(input.version) };
}
